import { HStack } from "@chakra-ui/react";
import { useAuth } from "modules/providers/auth-provider";
import { useUsers } from "modules/providers/users-provider";
import React, { memo, useCallback, useMemo } from "react";
import colors from "app/colors";
import {
  ReactionHeartColorIcon,
  ReactionLaughingColorIcon,
  ReactionSurpriseColorIcon,
  ReactionThumbsDownColorIcon,
  ReactionThumbsUpColorIcon,
} from "modules/icons";
import {
  FileGalleryFragment,
  MessageFragment,
  ReactionType,
} from "gql/graphql";
import { REACTION_ORDER } from "modules/utils/Constants";
import MessageReaction, { ReactionDisplay } from "./message-reaction";
import { getUserFullName } from "modules/utils/Functions";

interface IProps {
  message?: MessageFragment;
  file?: FileGalleryFragment;
}

const MessageReactions: React.FC<IProps> = ({ message, file }) => {
  const { me } = useAuth();
  const { usersById } = useUsers();

  const reactions = useMemo(() => {
    if (!message) return [];
    if (file) {
      return message.reactionList.filter(
        (reaction) => reaction.fileId === file.id,
      );
    }
    return message.reactionList.filter((reaction) => !reaction.fileId);
  }, [file, message]);

  const getReactionCount = useCallback(
    (type: ReactionType) =>
      reactions.filter((reaction) => reaction.type === type).length,
    [reactions],
  );

  const hasMe = useCallback(
    (type: ReactionType) => {
      if (file) {
        return reactions.some(
          (reaction) =>
            reaction.type === type &&
            reaction.userId === me?.id &&
            reaction.fileId === file.id,
        );
      }
      return reactions.some(
        (reaction) =>
          reaction.type === type &&
          reaction.userId === me?.id &&
          !reaction.fileId,
      );
    },
    [file, me?.id, reactions],
  );

  const getUserNameList = useCallback(
    (type: ReactionType) =>
      reactions
        .filter((reaction) => reaction.type === type)
        .map((reaction) => {
          const user = usersById[reaction.userId];
          if (user) {
            return getUserFullName(user);
          }
          return "";
        }),
    [reactions, usersById],
  );

  const getMeBackgroundColor = useCallback(
    (type: ReactionType) => {
      switch (type) {
        case ReactionType.Like:
        case ReactionType.Dislike:
          return file ? colors.blue60 : colors.blueLight;
        case ReactionType.Heart:
          return file ? colors.redDark : colors.redLight;
        case ReactionType.Laughing:
        case ReactionType.Surprise:
          return file ? colors.yellow40 : colors.yellowReaction;
        default:
          return colors.grayPrimary;
      }
    },
    [file],
  );

  const getMeBorderColor = useCallback(
    (type: ReactionType) => {
      switch (type) {
        case ReactionType.Like:
        case ReactionType.Dislike:
        case ReactionType.Heart:
          return file ? colors.gray50 : colors.grayPrimary;
        case ReactionType.Laughing:
        case ReactionType.Surprise:
          return colors.grayPrimary;
        default:
          return colors.grayPrimary;
      }
    },
    [file],
  );

  const getCountColor = useCallback(
    (type: ReactionType) => {
      switch (type) {
        case ReactionType.Like:
        case ReactionType.Dislike:
        case ReactionType.Heart:
          return file ? colors.white : colors.gray50;
        case ReactionType.Laughing:
        case ReactionType.Surprise:
          return colors.gray50;
        default:
          return colors.gray50;
      }
    },
    [file],
  );

  const getIcon = useCallback((type: ReactionType) => {
    switch (type) {
      case ReactionType.Like:
        return <ReactionThumbsUpColorIcon boxSize={4} />;
      case ReactionType.Dislike:
        return <ReactionThumbsDownColorIcon boxSize={4} />;
      case ReactionType.Heart:
        return <ReactionHeartColorIcon boxSize={4} />;
      case ReactionType.Laughing:
        return <ReactionLaughingColorIcon boxSize={4} />;
      case ReactionType.Surprise:
        return <ReactionSurpriseColorIcon boxSize={4} />;
      default:
        return null;
    }
  }, []);

  const reactionDisplays = useMemo(() => {
    if (!me) return;
    const reactionDisplays: ReactionDisplay[] = [];
    Object.entries(ReactionType)
      .sort((a, b) => {
        const indexA = REACTION_ORDER.indexOf(a[1]);
        const indexB = REACTION_ORDER.indexOf(b[1]);
        return indexA - indexB;
      })
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .forEach(([_, type]) => {
        reactionDisplays.push({
          type,
          hasMe: hasMe(type),
          meBackgroundColor: getMeBackgroundColor(type),
          meBorderColor: getMeBorderColor(type),
          userNameList: getUserNameList(type),
          count: getReactionCount(type),
          countColor: getCountColor(type),
          icon: getIcon(type),
        });
      });
    return reactionDisplays;
  }, [
    getCountColor,
    getIcon,
    getMeBackgroundColor,
    getMeBorderColor,
    getReactionCount,
    getUserNameList,
    hasMe,
    me,
  ]);

  if (!message) {
    return null;
  }

  return (
    <HStack>
      {me &&
        reactionDisplays
          ?.filter((obj) => obj.count > 0)
          .map((reaction) => (
            <MessageReaction
              key={reaction.type}
              message={message}
              file={file}
              reaction={reaction}
            />
          ))}
    </HStack>
  );
};

export default memo(MessageReactions);
